import React from 'react';
import { Card } from '../Card/Card';
import { t } from '../../locales/translate';
import { Option } from '../Form/Select/Select';
import { Stack } from '../Stack/Stack';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormForm } from '../Form/FormForm';
import { FormSelect } from '../Form/Select/FormSelect';
import { Spacer } from '../Spacer/Spacer';
import { Seatmap } from '../../model/seatmap/seatmap';
import { groupBy } from '../../util/collections';
import { PricingCategory, Seat } from '../../model/seatmap/types';
import { Selection } from '../../model/seatmap/selection';
import { handleError } from '../../model/notification';

interface Props {
  seatmap: Seatmap;
  onChange: () => void;
  onClose: () => void;
}

interface FormModel {
  pricingCategoryId: string;
}

export function SeatmapEditCategoryBox({ seatmap, onChange, onClose }: Props): JSX.Element {
  const selection = seatmap.selection;
  const categoriesAmountInSelection = groupBy(Array.from(selection.seats), (seat) => seat.pricingCategory);
  const currentPricingCategoryId = getCurrentPricingCategoryId(categoriesAmountInSelection, selection) ?? 'unknown';

  const { fields, onSubmit, valid, submissionStatus, dirty } = useForm<FormModel>({
    _unstableUpdateModelOnChange: true,
    model: {
      pricingCategoryId: currentPricingCategoryId,
    },
    onSubmit: async () => {
      try {
        await seatmap.changeSelectionPricingCategory(
          seatmap.layout.pricingCategories.find((e) => e.id === fields.pricingCategoryId.value)!
        );
        onChange();
        onClose();
      } catch (e) {
        handleError(e);
      }
    },
    validations: {
      pricingCategoryId: [
        ({ model }) => {
          if (model.pricingCategoryId === 'multiple') {
            return [t.model.venueLayout.pleaseSelectPricingCategory];
          }
          if (model.pricingCategoryId === 'unknown') {
            return ['required'];
          }
          return [];
        },
        'required',
      ],
    },
  });
  return (
    <Card title={t.model.pricingCategory._name} size="xs">
      <Stack size="xs">
        {selection.section && <div>{selection.section.name}</div>}
        {categoriesAmountInSelection.length > 0 && (
          <div className="seatmap-edit-category-box__list">
            <ul>
              {categoriesAmountInSelection.map(([category, seats]) => (
                <li key={category.id}>
                  {category.name}: {seats.length}
                </li>
              ))}
            </ul>
          </div>
        )}

        <FormForm
          onSubmit={onSubmit}
          canSubmit={valid && dirty}
          loading={submissionStatus === 'submitting'}
          onClose={onClose}
          buttonSize="small"
        >
          <FormSelect
            {...fields.pricingCategoryId}
            label={t.model.pricingCategory._name}
            options={getPricingCategoryOptions(
              seatmap.layout.pricingCategories,
              categoriesAmountInSelection.length > 1
            )}
            onChange={(val) => val && fields.pricingCategoryId.onChange(val)}
            allowEmpty={fields.pricingCategoryId.value === 'unknown'}
          ></FormSelect>
          <Spacer></Spacer>
        </FormForm>
      </Stack>
    </Card>
  );
}

function getPricingCategoryOptions(pricingCategories: PricingCategory[], mutiple?: boolean): Option[] {
  const options: Option[] = mutiple
    ? [{ value: 'multiple', disabled: true, label: t.model.venueLayout.multiplePricingCategories }]
    : [];
  pricingCategories.forEach((e) => {
    options.push({ value: e.id, label: e.name });
  });
  return options;
}

function getCurrentPricingCategoryId(list: [PricingCategory, Seat[]][], selection: Selection): string | null {
  if (list.length > 1) return 'multiple';
  return selection.section?.pricingCategory?.id ?? list[0]?.[0]?.id ?? null;
}
